import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  CalendarOff,
  CalendarRange,
  Moon,
  PartyPopper,
  Download,
  FileSpreadsheet,
  FileText,
  Users,
} from 'lucide-react';
import {
  addDays,
  fmtDateShort,
  fmtTime,
  fmtWeekday,
  localYmd,
  mondayOf,
  type Ymd,
} from '@siva/shared';
import { api, download } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert } from '../../lib/ui.js';
import { Avatar, Badge, Card, EmptyState, Skeleton, StatTile, Tabs } from '../../lib/components.js';
import { usePhotos } from '../photos.js';
import { DateField } from '../../lib/DateField.js';
import { MonthField } from '../../lib/MonthField.js';
import { AdjustButton, AdjustDay, type AdjustRequest } from '../AdjustDay.js';
import { t } from '../../i18n/index.js';

type Preset = 'day' | 'week' | 'month' | 'custom';

type WorkerRow = {
  user_id: string;
  worker: string;
  type: string;
  /** '1st Shift' / '2nd Shift'. The rows arrive grouped by it. */
  shift: string;
  present_days: number;
  absent_days: number;
  off_days: number;
  holiday_days: number;
  no_punchout: number;
  worked_minutes: number;
  target_minutes: number;
  shortfall_minutes: number;
  /** Against the office hours in Settings. Informational: they change no total here. */
  early_out: number;
};
type DayRow = {
  date: string;
  status_label: string;
  pill: string;
  first_in: string | null;
  last_out: string | null;
  auto_closed: boolean;
  worked_minutes: number;
  target_minutes: number;
  shortfall_minutes: number;
  early_out: { reason: string; status: 'pending' | 'approved' | 'rejected' } | null;
};
type Report = {
  from: string;
  to: string;
  timezone: string;
  scope: 'all' | 'worker';
  worker: { id: string; name: string } | null;
  workers: WorkerRow[];
  days: DayRow[];
  summary: {
    workers: number;
    days: number;
    present_days: number;
    absent_days: number;
    off_days: number;
    holiday_days: number;
    no_punchout: number;
    worked_minutes: number;
    target_minutes: number;
    shortfall_minutes: number;
  };
};

const hm = (min: number) =>
  min > 0 ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : '—';
const firstOfMonth = (ymd: Ymd) => `${ymd.slice(0, 7)}-01`;
const lastOfMonth = (ymd: Ymd) => {
  const [y, m] = ymd.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};
const TONE: Record<string, 'green' | 'amber' | 'violet' | 'red' | 'neutral' | 'blue'> = {
  present: 'green',
  in_progress: 'green',
  below: 'amber',
  no_punchout: 'violet',
  absent: 'red',
  off: 'neutral',
  holiday: 'blue',
};

/** Attendance over any range, for everyone or one worker, with PDF / XLSX exports. */
export function ReportsPage() {
  const { photoOf } = usePhotos();
  const today = localYmd(new Date());
  const [params, setParams] = useSearchParams();
  const [preset, setPreset] = useState<Preset>(
    params.get('from') && params.get('to') ? 'custom' : 'month',
  );
  const [from, setFrom] = useState<Ymd>(params.get('from') ?? firstOfMonth(today));
  const [to, setTo] = useState<Ymd>(params.get('to') ?? today);
  const [userId, setUserId] = useState(params.get('user_id') ?? '');
  const [adjust, setAdjust] = useState<AdjustRequest | null>(null);
  const [workers, setWorkers] = useState<
    { id: string; name: string; employee_code?: string | null }[]
  >([]);
  const [report, setReport] = useState<Report | null>(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState<'pdf' | 'xlsx' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<{ workers: { id: string; name: string; employee_code?: string | null }[] }>(
      '/admin/workers',
    )
      .then((r) => setWorkers(r.workers.map((w) => ({ id: w.id, name: w.name }))))
      .catch(() => setWorkers([]));
  }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'day') {
      setFrom(today);
      setTo(today);
    } else if (p === 'week') {
      const monday = mondayOf(today);
      setFrom(monday);
      setTo(addDays(monday, 6) > today ? today : addDays(monday, 6));
    } else if (p === 'month') {
      setFrom(firstOfMonth(today));
      setTo(lastOfMonth(today) > today ? today : lastOfMonth(today));
    }
  };

  const query = useMemo(() => {
    const q = new URLSearchParams({ from, to });
    if (userId) q.set('user_id', userId);
    return q.toString();
  }, [from, to, userId]);

  const generate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<Report>(`/admin/reports/range?${query}`);
      setReport(r);
      setParams(new URLSearchParams(query), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setReport(null);
    } finally {
      setBusy(false);
    }
  }, [query, setParams]);

  // A report the URL already describes is generated on arrival.
  useEffect(() => {
    if (params.get('from') && params.get('to')) void generate();
  }, []);

  const exportAs = async (format: 'pdf' | 'xlsx') => {
    setDl(format);
    setError(null);
    try {
      const name = userId ? `attendance-${from}-to-${to}-worker` : `attendance-${from}-to-${to}`;
      await download(`/admin/reports/range?${query}&format=${format}`, `${name}.${format}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDl(null);
    }
  };

  const s = report?.summary;
  return (
    <div className="stack">
      <AdjustDay request={adjust} onClose={() => setAdjust(null)} onSaved={() => void generate()} />
      <Card
        title={t('nav_reports')}
        subtitle={t('reports_sub')}
        actions={
          report && (
            <span className="row">
              <button
                type="button"
                className="secondary"
                onClick={() => void exportAs('pdf')}
                disabled={dl !== null}
              >
                <FileText size={16} aria-hidden="true" /> {t('reports_pdf')}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => void exportAs('xlsx')}
                disabled={dl !== null}
              >
                <FileSpreadsheet size={16} aria-hidden="true" /> {t('reports_xlsx')}
              </button>
            </span>
          )
        }
      >
        <form
          className="report-controls"
          onSubmit={(e) => {
            e.preventDefault();
            void generate();
          }}
        >
          <Tabs
            value={preset}
            onChange={(p) => applyPreset(p as Preset)}
            items={[
              { value: 'day', label: t('reports_day') },
              { value: 'week', label: t('reports_week') },
              { value: 'month', label: t('reports_month') },
              { value: 'custom', label: t('reports_custom') },
            ]}
          />
          {/* One control per preset. From and To under "Day" was two pickers for one date,
              which the owner rightly called senseless: a day takes a date, a week takes any
              date in it, a month takes a month, and only Custom takes a range. */}
          {preset === 'day' && (
            <label className="inline-field">
              {t('reports_date')}
              <DateField
                value={from}
                max={today}
                onChange={(v) => {
                  setFrom(v);
                  setTo(v);
                }}
              />
            </label>
          )}
          {preset === 'week' && (
            <label className="inline-field">
              {t('reports_week_of')}
              <DateField
                value={from}
                max={today}
                onChange={(v) => {
                  const monday = mondayOf(v);
                  setFrom(monday);
                  setTo(addDays(monday, 6) > today ? today : addDays(monday, 6));
                }}
              />
            </label>
          )}
          {preset === 'month' && (
            <label className="inline-field">
              {t('reports_month_label')}
              <MonthField
                value={from}
                max={today}
                label={t('reports_month_label')}
                onChange={(first) => {
                  setFrom(first);
                  setTo(lastOfMonth(first) > today ? today : lastOfMonth(first));
                }}
              />
            </label>
          )}
          {preset === 'custom' && (
            <>
              <label className="inline-field">
                {t('reports_from')}
                <DateField value={from} max={to} onChange={setFrom} />
              </label>
              <label className="inline-field">
                {t('reports_to')}
                <DateField value={to} min={from} max={today} onChange={setTo} />
              </label>
            </>
          )}
          <label className="inline-field">
            {t('reports_worker')}
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">{t('reports_all_workers')}</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {/* Two workers can share a name; the id is what tells them apart. */}
                  {w.name}
                  {w.employee_code ? ` · ${w.employee_code}` : ''}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy}>
            <Download size={16} aria-hidden="true" /> {t('reports_generate')}
          </button>
        </form>
        {error && <Alert>{error}</Alert>}
      </Card>

      {busy && !report && <Skeleton lines={6} />}

      {!busy && !report && (
        <Card>
          <EmptyState icon={BarChart3} text={t('reports_start')} />
        </Card>
      )}

      {report && s && (
        <>
          {/* Day counts only where a day count means something: one worker's days over the
              range, or everybody on one day. Over several days for everybody the tiles said
              "Absent 39" — worker-days — and the owner rightly asked how, with twelve workers.
              The table underneath carries each person's own days. */}
          {report.scope === 'worker' || s.days === 1 ? (
            <div className="stat-row">
              <StatTile
                icon={report.scope === 'worker' ? CalendarRange : Users}
                tone="teal"
                label={report.scope === 'worker' ? t('reports_days') : t('reports_workers')}
                value={report.scope === 'worker' ? s.days : s.workers}
              />
              <StatTile
                icon={CalendarRange}
                tone="green"
                label={report.scope === 'worker' ? t('reports_present_days') : t('att_present')}
                value={s.present_days}
              />
              <StatTile icon={Users} tone="red" label={t('att_absent')} value={s.absent_days} />
              <StatTile icon={CalendarOff} tone="neutral" label={t('att_off')} value={s.off_days} />
              <StatTile
                icon={PartyPopper}
                tone="blue"
                label={t('hours_st_holiday')}
                value={s.holiday_days}
              />
              <StatTile
                icon={Moon}
                tone="violet"
                label={t('att_no_punchout')}
                value={s.no_punchout}
              />
            </div>
          ) : (
            <div className="stat-row two">
              <StatTile icon={Users} tone="teal" label={t('reports_workers')} value={s.workers} />
              <StatTile
                icon={CalendarRange}
                tone="neutral"
                label={t('reports_days')}
                value={s.days}
              />
            </div>
          )}

          <Card
            title={
              report.scope === 'worker'
                ? (report.worker?.name ?? t('nav_reports'))
                : t('reports_by_worker')
            }
            subtitle={`${fmtDateShort(report.from)} – ${fmtDateShort(report.to)}`}
          >
            {report.scope === 'worker' ? (
              report.days.length === 0 ? (
                <EmptyState text={t('reports_empty')} />
              ) : (
                <div className="table-wrap">
                  <table className="stacks">
                    <thead>
                      <tr>
                        <th>{t('reports_date')}</th>
                        <th>{t('worker_status')}</th>
                        <th>{t('reports_in')}</th>
                        <th>{t('reports_out')}</th>
                        <th>{t('reports_hours')}</th>
                        <th>{t('reports_target')}</th>
                        <th>{t('reports_shortfall')}</th>
                        <th className="sr-only">{t('adj_open')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.days.map((d) => (
                        <tr key={d.date}>
                          <td className="nowrap cell-name">
                            {fmtDateShort(d.date)}{' '}
                            <span className="muted">{fmtWeekday(d.date)}</span>
                          </td>
                          <td data-label={t('worker_status')}>
                            <Badge tone={TONE[d.pill] ?? 'neutral'} className="no-ml">
                              {d.status_label}
                            </Badge>
                          </td>
                          <td className="nowrap" data-label={t('reports_in')}>
                            {d.first_in ? fmtTime(d.first_in, report.timezone) : ''}
                          </td>
                          <td className="nowrap" data-label={t('reports_out')}>
                            {d.last_out
                              ? fmtTime(d.last_out, report.timezone)
                              : d.first_in && d.auto_closed
                                ? '—'
                                : ''}
                            {d.early_out && (
                              <Badge
                                tone={d.early_out.status === 'approved' ? 'green' : 'amber'}
                                className="no-ml"
                              >
                                {t('reports_early_out')}
                              </Badge>
                            )}
                          </td>
                          <td className="nowrap" data-label={t('reports_hours')}>
                            {hm(d.worked_minutes)}
                          </td>
                          <td className="nowrap" data-label={t('reports_target')}>
                            {hm(d.target_minutes)}
                          </td>
                          <td
                            className={`nowrap ${d.shortfall_minutes ? 'short' : ''}`}
                            data-label={t('reports_shortfall')}
                          >
                            {d.shortfall_minutes ? hm(d.shortfall_minutes) : ''}
                          </td>
                          {/* A wrong day is most often noticed while reading the report it
                              spoils. Correcting it from here beats memorising the date and
                              going to find the worker. */}
                          <td className="nowrap actions" data-label={t('adj_open')}>
                            <AdjustButton
                              className="ghost small row-action"
                              label={t('adj_open')}
                              onClick={() =>
                                setAdjust({ kind: 'one', workerId: userId, date: d.date })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : report.workers.length === 0 ? (
              <EmptyState text={t('reports_empty')} />
            ) : (
              <div className="table-wrap">
                <table className="stacks">
                  <thead>
                    <tr>
                      <th>{t('worker_name')}</th>
                      <th>{t('worker_type')}</th>
                      <th>{t('reports_present_days')}</th>
                      <th>{t('att_absent')}</th>
                      <th>{t('att_off')}</th>
                      <th>{t('hours_st_holiday')}</th>
                      <th>{t('att_no_punchout')}</th>
                      <th>{t('reports_early_out')}</th>
                      <th>{t('reports_hours')}</th>
                      {/* Target and Shortfall: the rows have carried both since 4 October; the headings
                          went into the per-day table instead, twice, and this one had none. */}
                      <th>{t('reports_target')}</th>
                      <th>{t('reports_shortfall')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.workers.map((w) => (
                      <tr key={w.user_id}>
                        <td className="cell-name">
                          <span className="person">
                            <Avatar name={w.worker} size={26} photoUrl={photoOf(w.user_id)} />
                            <span>{w.worker}</span>
                          </span>
                        </td>
                        <td className="nowrap" data-label={t('worker_type')}>
                          {w.type}
                          {/* Only when the range holds both: a marker that never varies is noise.
                              The rows are already in shift order, so this labels the run. */}
                          {new Set(report.workers.map((x) => x.shift)).size > 1 && (
                            <span className="muted"> · {w.shift.replace(' Shift', '')}</span>
                          )}
                        </td>
                        <td data-label={t('reports_present_days')}>{w.present_days}</td>
                        <td data-label={t('att_absent')}>{w.absent_days}</td>
                        <td data-label={t('att_off')}>{w.off_days}</td>
                        <td data-label={t('hours_st_holiday')}>{w.holiday_days}</td>
                        <td data-label={t('att_no_punchout')}>{w.no_punchout}</td>
                        <td data-label={t('reports_early_out')}>{w.early_out}</td>
                        <td className="nowrap" data-label={t('reports_hours')}>
                          {hm(w.worked_minutes)}
                        </td>
                        <td className="nowrap" data-label={t('reports_target')}>
                          {hm(w.target_minutes)}
                        </td>
                        <td className="nowrap" data-label={t('reports_shortfall')}>
                          {w.shortfall_minutes > 0 ? hm(w.shortfall_minutes) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
